<script setup lang="ts">
import { api } from "@/api";
import { PV } from "@/widgets";
import { computed, ref } from "vue";
import TrashIcon from "~icons/mdi/trash-outline";
import { formatRupiah } from "../../common/formats.common";
import type { ProductHotspot } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { FormInputSelect } from "../input";
import { FormMessage, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = ProductHotspot[];
interface PropsType extends BaseProps<ModelType> {
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value.length > 0,
});

const actions = defineActions(model, props, state);
const selectModel = ref<ProductHotspot>();
const selectedProducts = computed(() => new Set(model.value.map((p) => p.id)));

function onInsert() {
    state.errorMsg = undefined;
    state.hasError = false;

    if (selectModel.value === undefined) {
        return;
    } else if (selectedProducts.value.has(selectModel.value.id)) {
        state.errorMsg = `Produk ${selectModel.value.name} sudah ditambahkan.`;
        state.hasError = true;
        return;
    }

    model.value.push(selectModel.value);
    selectModel.value = undefined;
}

function onRemove(index: number) {
    selectedProducts.value.delete(model.value[index].id);
    model.value.splice(index, 1);
}
</script>

<template>
    <div class="flex flex-col gap-2 overflow-x-hidden">
        <div class="flex flex-row justify-between pl-1 pr-2.5">
            <label class="font-semibold text-gray-600">{{ props.label }}</label>
            <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
        </div>

        <template v-if="!state.editMode">
            <div class="w-full overflow-x-auto">
                <table class="w-full border">
                    <thead>
                        <tr class="bg-gray-800 text-white rns-table-header">
                            <th>No.</th>
                            <th>Nama</th>
                            <th>Harga Jual</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(product, index) in model" class="rns-table-content">
                            <td class="w-1/12 text-center">{{ index + 1 }}.</td>
                            <td class="w-6/12">{{ product.name }}</td>
                            <td class="w-5/12">{{ formatRupiah(product.price + product.profit) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
        <template v-else>
            <div class="w-full flex flex-col md:flex-row gap-2">
                <FormInputSelect
                    v-model="selectModel"
                    class="grow"
                    embedMode="inline"
                    label="Produk Hotspot"
                    :options="api.productHotspot.formHooks.usable"
                    searchable
                />

                <PV.Button label="Tambah" @click="onInsert" />
            </div>

            <div class="w-full overflow-x-auto">
                <table class="w-full border">
                    <thead>
                        <tr class="bg-gray-800 text-white rns-table-header">
                            <th>No.</th>
                            <th>Nama</th>
                            <th>Harga Jual</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody v-if="model.length > 0">
                        <tr v-for="(product, index) in model" class="rns-table-content">
                            <td class="w-1/12 text-center">{{ index + 1 }}.</td>
                            <td class="w-5/12">{{ product.name }}</td>
                            <td class="w-5/12">{{ formatRupiah(product.price + product.profit) }}</td>
                            <td class="w-1/12">
                                <div class="w-full flex flex-row items-center justify-center">
                                    <TrashIcon
                                        class="text-red-500 hover:cursor-pointer"
                                        @click="() => onRemove(index)"
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td class="text-center p-2" colspan="4">Tidak ada produk yang dipilih.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-table-header th {
    @apply border border-gray-800 px-3 py-1;
}

.rns-table-content td {
    @apply border border-gray-400 px-3 py-2;
}
</style>
